import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import PDFPreview from "../components/PDFPreview";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Building2, Plus, Search, Eye, MoreHorizontal, CheckCircle, XCircle, Pause, Upload, FileText, X, AlertTriangle, Mail, Download, FolderOpen, Award, Copy, Key, ShieldCheck, Loader2, FileSpreadsheet, RefreshCw, Send } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import SendEmailDialog from "../components/SendEmailDialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  "Conakry",
  "Boké",
  "Faranah", 
  "Kankan",
  "Kindia",
  "Labé",
  "Mamou",
  "N'Zérékoré"
];

export default function CabinetsPage() {
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // État pour le dialog de suspension/révocation
  const [statusChangeDialog, setStatusChangeDialog] = useState({ open: false, cabinetId: null, newStatus: null, cabinetName: "" });
  const [motifChangement, setMotifChangement] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [documents, setDocuments] = useState([]); // Liste des documents uploadés
  // État pour l'envoi d'emails
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedCabinets, setSelectedCabinets] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  // État pour visualiser les documents d'un cabinet
  const [documentsDialog, setDocumentsDialog] = useState({ open: false, cabinet: null, documents: [], loading: false });
  // État pour la prévisualisation PDF
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, filename: null });
  // État pour la validation et affichage des identifiants
  const [credentialsDialog, setCredentialsDialog] = useState({ open: false, credentials: null });
  const [validating, setValidating] = useState(false);
  // État pour le dialog de validation avec observations
  const [validationDialog, setValidationDialog] = useState({ open: false, cabinet: null });
  const [validationObservations, setValidationObservations] = useState("");
  // État pour le dialog de modification
  const [editDialog, setEditDialog] = useState({ open: false, cabinet: null });
  const [editFormData, setEditFormData] = useState({
    nom: "",
    secteur_activite: "",
    region: "",
    adresse: "",
    telephone: "",
    email: "",
    responsable_nom: "",
    responsable_prenom: "",
  });
  const [editDocuments, setEditDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  // État pour la réinitialisation du mot de passe
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, cabinet: null, loading: false, result: null });
  // État pour le dialog de renvoi d'email d'habilitation
  const [resendHabilitationDialog, setResendHabilitationDialog] = useState({ open: false, cabinet: null, loading: false, result: null });
  const [formData, setFormData] = useState({
    nom: "",
    rccm: "",
    nif: "",
    id_igt: "",
    secteur_activite: "",
    region: "",
    adresse: "",
    telephone: "",
    email: "",
    responsable_nom: "",
    responsable_prenom: "",
    documents_procedure: [], // Array of {type, filename, original_name}
  });

  const DOCUMENT_TYPES = [
    { value: "rccm", label: "RCCM" },
    { value: "demande", label: "Demande d'habilitation" },
    { value: "statuts", label: "Statuts de l'entreprise" },
    { value: "preuve_paiement", label: "Preuve de paiement" },
    { value: "preuve_local", label: "Preuve de local" },
    { value: "document_fiscal", label: "Document fiscal" },
    { value: "preuve_experience", label: "Preuve d'expérience" },
    { value: "autre", label: "Autre document" },
  ];

  useEffect(() => {
    fetchCabinets();
  }, []);

  const fetchCabinets = async () => {
    try {
      const response = await axios.get(`${API_URL}/cabinets`);
      setCabinets(response.data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch cabinets:", error);
      toast.error("Erreur lors du chargement des cabinets");
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (e, documentType) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate each file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const validFiles = [];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Type non autorisé. Utilisez PDF, JPG ou PNG.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Fichier trop volumineux (max 10MB)`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploadingFile(true);

    try {
      const uploadedDocs = [];
      
      for (const file of validFiles) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API_URL}/cabinets/upload-document`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const newDoc = {
          type: documentType,
          filename: response.data.filename,
          original_name: file.name,
          label: DOCUMENT_TYPES.find(d => d.value === documentType)?.label || documentType
        };
        
        uploadedDocs.push(newDoc);
      }

      setDocuments(prev => [...prev, ...uploadedDocs]);
      setFormData(prev => ({
        ...prev,
        documents_procedure: [...prev.documents_procedure, ...uploadedDocs]
      }));
      
      const docLabel = DOCUMENT_TYPES.find(d => d.value === documentType)?.label || documentType;
      toast.success(`${uploadedDocs.length} fichier(s) "${docLabel}" téléchargé(s) avec succès`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
    } finally {
      setUploadingFile(false);
      // Reset input to allow re-uploading same files
      e.target.value = '';
    }
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      documents_procedure: prev.documents_procedure.filter((_, i) => i !== index)
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API_URL}/cabinets`, formData);
      toast.success("Cabinet créé avec succès");
      setIsCreateOpen(false);
      setDocuments([]);
      setFormData({
        nom: "",
        rccm: "",
        nif: "",
        id_igt: "",
        secteur_activite: "",
        region: "",
        adresse: "",
        telephone: "",
        email: "",
        responsable_nom: "",
        responsable_prenom: "",
        documents_procedure: [],
      });
      fetchCabinets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (cabinetId, newStatus, motif = "") => {
    // Pour suspension ou révocation, on doit passer par le dialog
    if ((newStatus === "suspendu" || newStatus === "revoque") && !motif) {
      const cabinet = cabinets.find(c => c.id === cabinetId);
      setStatusChangeDialog({
        open: true,
        cabinetId,
        newStatus,
        cabinetName: cabinet?.nom || ""
      });
      return;
    }
    
    try {
      await axios.patch(`${API_URL}/cabinets/${cabinetId}`, { 
        status: newStatus,
        motif_changement_statut: motif || undefined
      });
      toast.success(
        newStatus === "actif" ? "Cabinet réactivé avec succès" :
        newStatus === "suspendu" ? "Cabinet suspendu. Le cabinet a été notifié par email." :
        "Cabinet révoqué. Le cabinet a été notifié par email."
      );
      fetchCabinets();
      setStatusChangeDialog({ open: false, cabinetId: null, newStatus: null, cabinetName: "" });
      setMotifChangement("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const confirmStatusChange = () => {
    if (!motifChangement.trim()) {
      toast.error("Le motif est obligatoire");
      return;
    }
    handleStatusChange(statusChangeDialog.cabinetId, statusChangeDialog.newStatus, motifChangement);
  };

  // Fonction pour charger les documents d'un cabinet
  const fetchCabinetDocuments = async (cabinet) => {
    setDocumentsDialog({ open: true, cabinet, documents: [], loading: true });
    try {
      const response = await axios.get(`${API_URL}/cabinets/${cabinet.id}/documents`);
      setDocumentsDialog(prev => ({ ...prev, documents: response.data.documents, loading: false }));
    } catch (error) {
      toast.error("Erreur lors du chargement des documents");
      setDocumentsDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Fonction pour prévisualiser un document PDF
  const previewDocument = async (downloadUrl, filename) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${downloadUrl}`, {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfPreview({ open: true, url: blobUrl, filename });
    } catch (error) {
      toast.error("Erreur lors du chargement du document");
    }
  };

  // Fermer la prévisualisation PDF
  const closePdfPreview = () => {
    if (pdfPreview.url) {
      window.URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview({ open: false, url: null, filename: null });
  };

  // Fonction pour télécharger un document
  const downloadDocument = async (downloadUrl, filename) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${downloadUrl}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Document téléchargé");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Fonction pour télécharger le PDF d'habilitation d'un cabinet
  const downloadHabilitationPdf = async (cabinet) => {
    try {
      toast.info("Génération du PDF d'habilitation en cours...");
      const response = await axios.get(`${API_URL}/cabinets/${cabinet.id}/habilitation-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Habilitation_${cabinet.nom.replace(/\s+/g, '_')}_${cabinet.id_igt || 'N-A'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF d'habilitation téléchargé avec succès");
    } catch (error) {
      console.error(/* PROD */ "Erreur téléchargement habilitation:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement du PDF d'habilitation");
    }
  };

  // Fonction pour ouvrir le dialog de validation
  const openValidationDialog = (cabinet) => {
    setValidationDialog({ open: true, cabinet });
    setValidationObservations("");
  };

  // Fonction pour ouvrir le dialog de modification
  const openEditDialog = (cabinet) => {
    setEditFormData({
      nom: cabinet.nom || "",
      secteur_activite: cabinet.secteur_activite || "",
      region: cabinet.region || "",
      adresse: cabinet.adresse || "",
      telephone: cabinet.telephone || "",
      email: cabinet.email || "",
      responsable_nom: cabinet.responsable_nom || "",
      responsable_prenom: cabinet.responsable_prenom || "",
    });
    setEditDocuments(cabinet.documents_procedure || []);
    setEditDialog({ open: true, cabinet });
  };

  // Fonction pour sauvegarder les modifications
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...editFormData,
        documents_procedure: editDocuments
      };
      
      await axios.put(`${API_URL}/cabinets/${editDialog.cabinet.id}`, updateData);
      toast.success("Cabinet modifié avec succès");
      setEditDialog({ open: false, cabinet: null });
      fetchCabinets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  // Fonction pour ajouter des documents au cabinet existant
  const handleEditDocumentUpload = async (e, documentType) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const validFiles = [];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Type non autorisé`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Fichier trop volumineux`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploadingFile(true);
    try {
      const uploadedDocs = [];
      
      for (const file of validFiles) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API_URL}/cabinets/upload-document`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        uploadedDocs.push({
          type: documentType,
          filename: response.data.filename,
          original_name: file.name,
          label: DOCUMENT_TYPES.find(d => d.value === documentType)?.label || documentType
        });
      }

      setEditDocuments(prev => [...prev, ...uploadedDocs]);
      toast.success(`${uploadedDocs.length} fichier(s) ajouté(s)`);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const removeEditDocument = (index) => {
    setEditDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Fonction pour exporter la liste des cabinets
  const exportCabinets = async (format) => {
    try {
      toast.info(`Génération du fichier ${format.toUpperCase()} en cours...`);
      
      const response = await axios.get(`${API_URL}/cabinets/export?format=${format}`, {
        responseType: 'blob'
      });
      
      const contentType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Liste_Cabinets_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Liste des cabinets exportée en ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erreur lors de l'export ${format.toUpperCase()}`);
    }
  };

  // Fonction pour valider un cabinet pré-autorisé
  const handleValidateCabinet = async () => {
    const cabinet = validationDialog.cabinet;
    if (!cabinet) return;
    
    setValidating(true);
    try {
      const response = await axios.post(`${API_URL}/cabinets/${cabinet.id}/validate`, {
        observations: validationObservations || null
      });
      toast.success(response.data.message);
      
      // Fermer le dialog de validation
      setValidationDialog({ open: false, cabinet: null });
      setValidationObservations("");
      
      // Afficher les identifiants dans un dialog
      if (response.data.credentials) {
        setCredentialsDialog({
          open: true,
          credentials: response.data.credentials
        });
      }
      
      fetchCabinets();
    } catch (error) {
      console.error(/* PROD */ "Erreur validation:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidating(false);
    }
  };

  // Fonction pour copier les identifiants dans le presse-papier
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié dans le presse-papier`);
  };

  // Fonction pour télécharger le PDF des identifiants
  const downloadCredentialsPdf = async (cabinetId) => {
    try {
      toast.info("Génération du PDF des identifiants...");
      const response = await axios.get(`${API_URL}/cabinets/${cabinetId}/credentials-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Identifiants_Cabinet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF des identifiants téléchargé");
    } catch (error) {
      console.error(/* PROD */ "Erreur téléchargement identifiants:", error);
      toast.error("Erreur lors du téléchargement du PDF");
    }
  };

  // Fonction pour réinitialiser le mot de passe d'un cabinet
  const resetCabinetPassword = async (cabinet, sendEmail = true) => {
    setResetPasswordDialog({ open: true, cabinet, loading: true, result: null });
    try {
      const response = await axios.post(`${API_URL}/cabinets/${cabinet.id}/reset-password?send_email=${sendEmail}`);
      setResetPasswordDialog({ 
        open: true, 
        cabinet, 
        loading: false, 
        result: response.data 
      });
      toast.success("Mot de passe réinitialisé avec succès");
    } catch (error) {
      console.error(/* PROD */ "Erreur réinitialisation:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
      setResetPasswordDialog({ open: false, cabinet: null, loading: false, result: null });
    }
  };

  // Fonction pour renvoyer l'email d'habilitation
  const resendHabilitationEmail = async (cabinet) => {
    setResendHabilitationDialog({ open: true, cabinet, loading: true, result: null });
    try {
      const response = await axios.post(`${API_URL}/cabinets/${cabinet.id}/resend-habilitation-email?reset_password=true`);
      setResendHabilitationDialog({ 
        open: true, 
        cabinet, 
        loading: false, 
        result: response.data 
      });
      toast.success("Email d'habilitation renvoyé avec succès");
    } catch (error) {
      console.error(/* PROD */ "Erreur envoi email habilitation:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de l'email d'habilitation");
      setResendHabilitationDialog({ open: false, cabinet: null, loading: false, result: null });
    }
  };

  const filteredCabinets = cabinets.filter((cabinet) => {
    // Exclure le cabinet test 0126BS
    if (cabinet.id_igt === "0126BS") return false;
    
    const matchesSearch =
      cabinet.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cabinet.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cabinet.rccm.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || cabinet.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      actif: "badge-success",
      pre_autorise: "badge-info",
      suspendu: "badge-warning",
      revoque: "badge-error",
    };
    const labels = {
      actif: "Actif",
      pre_autorise: "Pré-autorisé",
      suspendu: "Suspendu",
      revoque: "Révoqué",
    };
    return <span className={badges[status] || "badge-neutral"}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16" data-testid="cabinets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Cabinets Habilités</h1>
          <p className="text-muted-foreground mt-1">
            Gestion des cabinets autorisés à déclarer les bilans sociaux
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Bouton Export */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCabinets('excel')}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Exporter en Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCabinets('pdf')}>
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exporter en PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Bouton Nouveau Cabinet */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="create-cabinet-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Cabinet
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Créer un Cabinet</DialogTitle>
              <DialogDescription>
                Enregistrez un nouveau cabinet habilité sur la plateforme
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-2 gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="col-span-2">
                  <Label htmlFor="nom">Nom du cabinet *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    data-testid="cabinet-nom-input"
                  />
                </div>
                <div>
                  <Label htmlFor="rccm">RCCM *</Label>
                  <Input
                    id="rccm"
                    value={formData.rccm}
                    onChange={(e) => setFormData({ ...formData, rccm: e.target.value })}
                    required
                    data-testid="cabinet-rccm-input"
                  />
                </div>
                <div>
                  <Label htmlFor="nif">NIF *</Label>
                  <Input
                    id="nif"
                    value={formData.nif}
                    onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                    required
                    data-testid="cabinet-nif-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="id_igt">ID IGT *</Label>
                  <Input
                    id="id_igt"
                    value={formData.id_igt}
                    onChange={(e) => setFormData({ ...formData, id_igt: e.target.value })}
                    placeholder="Numéro d'immatriculation à l'IGT"
                    required
                    data-testid="cabinet-id-igt-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="secteur_activite">Secteur d'activité *</Label>
                  <Select
                    value={formData.secteur_activite}
                    onValueChange={(value) => setFormData({ ...formData, secteur_activite: value })}
                  >
                    <SelectTrigger data-testid="cabinet-secteur-input">
                      <SelectValue placeholder="Sélectionner un secteur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BTP, Génie Civil, Immobilier">BTP, Génie Civil, Immobilier</SelectItem>
                      <SelectItem value="Agriculture, Agro-industrie, Elevage, Pêche">Agriculture, Agro-industrie, Elevage, Pêche</SelectItem>
                      <SelectItem value="Ambassade">Ambassade</SelectItem>
                      <SelectItem value="Automobile et Mécanique Générale">Automobile et Mécanique Générale</SelectItem>
                      <SelectItem value="Banques, IMF, Monnaies Electroniques, Assurances">Banques, IMF, Monnaies Electroniques, Assurances</SelectItem>
                      <SelectItem value="Diamantaires, Orpailleurs et Assimilés">Diamantaires, Orpailleurs et Assimilés</SelectItem>
                      <SelectItem value="Enseignement Privé">Enseignement Privé</SelectItem>
                      <SelectItem value="Environnement, Assainissement, Bois">Environnement, Assainissement, Bois</SelectItem>
                      <SelectItem value="EPA">EPA</SelectItem>
                      <SelectItem value="Hydrocarbure et énergie">Hydrocarbure et énergie</SelectItem>
                      <SelectItem value="Industrie Manufacturière, Commerce">Industrie Manufacturière, Commerce</SelectItem>
                      <SelectItem value="Institution">Institution</SelectItem>
                      <SelectItem value="Mines et Carrières">Mines et Carrières</SelectItem>
                      <SelectItem value="Prestation de Services, Bureau d'Etude Technique">Prestation de Services, Bureau d'Etude Technique</SelectItem>
                      <SelectItem value="SA Etatique">SA Etatique</SelectItem>
                      <SelectItem value="Santé">Santé</SelectItem>
                      <SelectItem value="Sécurité Privée">Sécurité Privée</SelectItem>
                      <SelectItem value="TIC, Télécom, Economie numérique, Presse">TIC, Télécom, Economie numérique, Presse</SelectItem>
                      <SelectItem value="Tourisme, Artisanat, Boulangerie, Patisserie">Tourisme, Artisanat, Boulangerie, Patisserie</SelectItem>
                      <SelectItem value="Transport, Transit, Consignation">Transport, Transit, Consignation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="region">Région *</Label>
                  <Select
                    value={formData.region}
                    onValueChange={(value) => setFormData({ ...formData, region: value })}
                  >
                    <SelectTrigger data-testid="cabinet-region-input">
                      <SelectValue placeholder="Sélectionner une région" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Conakry">Conakry</SelectItem>
                      <SelectItem value="Boké">Boké</SelectItem>
                      <SelectItem value="Kindia">Kindia</SelectItem>
                      <SelectItem value="Mamou">Mamou</SelectItem>
                      <SelectItem value="Labé">Labé</SelectItem>
                      <SelectItem value="Faranah">Faranah</SelectItem>
                      <SelectItem value="Kankan">Kankan</SelectItem>
                      <SelectItem value="Nzérékoré">Nzérékoré</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Documents de la procédure */}
                <div className="col-span-2">
                  <Label className="text-base font-semibold">Documents de la procédure d'habilitation</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Téléchargez les documents justificatifs. Vous pouvez ajouter plusieurs fichiers par catégorie (ex: plusieurs CV pour preuve d'expérience).
                  </p>
                  
                  {/* Zone d'upload par type de document */}
                  <div className="space-y-4">
                    {DOCUMENT_TYPES.map((docType) => {
                      const docsOfType = documents.filter(d => d.type === docType.value);
                      const docCount = docsOfType.length;
                      
                      return (
                        <div key={docType.value} className="border rounded-lg p-3 bg-slate-50">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="font-medium text-sm">{docType.label}</span>
                              {docCount > 0 && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                  {docCount} fichier{docCount > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <label
                              htmlFor={`doc-${docType.value}`}
                              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded-md cursor-pointer hover:bg-primary/90 transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                              Ajouter
                              <input
                                id={`doc-${docType.value}`}
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple
                                onChange={(e) => handleDocumentUpload(e, docType.value)}
                                disabled={uploadingFile}
                              />
                            </label>
                          </div>
                          
                          {/* Liste des fichiers pour ce type */}
                          {docCount > 0 && (
                            <div className="space-y-1 mt-2">
                              {docsOfType.map((doc, idx) => {
                                const globalIndex = documents.findIndex(d => d === doc);
                                return (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                      <span className="text-xs text-slate-600 truncate">{doc.original_name}</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeDocument(globalIndex)}
                                      className="p-1 hover:bg-red-50 rounded text-red-500 flex-shrink-0"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {uploadingFile && (
                    <div className="flex items-center gap-2 text-sm text-primary mt-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Téléchargement en cours...
                    </div>
                  )}
                  
                  {/* Résumé des documents */}
                  {documents.length > 0 && (
                    <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-sm text-emerald-700">
                        <strong>Total :</strong> {documents.length} document{documents.length > 1 ? 's' : ''} téléchargé{documents.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="adresse">Adresse *</Label>
                  <Input
                    id="adresse"
                    value={formData.adresse}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="telephone">Téléphone *</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="cabinet-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="responsable_prenom">Prénom du responsable *</Label>
                  <Input
                    id="responsable_prenom"
                    value={formData.responsable_prenom}
                    onChange={(e) => setFormData({ ...formData, responsable_prenom: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="responsable_nom">Nom du responsable *</Label>
                  <Input
                    id="responsable_nom"
                    value={formData.responsable_nom}
                    onChange={(e) => setFormData({ ...formData, responsable_nom: e.target.value })}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={creating} data-testid="submit-cabinet-btn">
                  {creating ? "Création..." : "Créer le cabinet"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog pour motif de suspension/révocation */}
        <Dialog open={statusChangeDialog.open} onOpenChange={(open) => {
          if (!open) {
            setStatusChangeDialog({ open: false, cabinetId: null, newStatus: null, cabinetName: "" });
            setMotifChangement("");
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className={`w-5 h-5 ${statusChangeDialog.newStatus === "revoque" ? "text-red-600" : "text-orange-500"}`} />
                {statusChangeDialog.newStatus === "suspendu" ? "Suspendre le cabinet" : "Révoquer le cabinet"}
              </DialogTitle>
              <DialogDescription>
                {statusChangeDialog.newStatus === "suspendu" 
                  ? `Vous êtes sur le point de suspendre le cabinet "${statusChangeDialog.cabinetName}". Cette action est réversible.`
                  : `Vous êtes sur le point de révoquer définitivement le cabinet "${statusChangeDialog.cabinetName}". Cette action est irréversible.`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="motif" className="text-sm font-medium">
                  Motif de la décision <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="motif"
                  placeholder="Veuillez expliquer le motif de cette décision..."
                  value={motifChangement}
                  onChange={(e) => setMotifChangement(e.target.value)}
                  className="mt-2 min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ce motif sera communiqué au cabinet par email.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setStatusChangeDialog({ open: false, cabinetId: null, newStatus: null, cabinetName: "" });
                  setMotifChangement("");
                }}
              >
                Annuler
              </Button>
              <Button
                variant={statusChangeDialog.newStatus === "revoque" ? "destructive" : "default"}
                onClick={confirmStatusChange}
                disabled={!motifChangement.trim()}
              >
                {statusChangeDialog.newStatus === "suspendu" ? "Suspendre" : "Révoquer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email ou RCCM..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-cabinets"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pre_autorise">Pré-autorisé</SelectItem>
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="suspendu">Suspendu</SelectItem>
                <SelectItem value="revoque">Révoqué</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant={selectionMode ? "default" : "outline"}
              onClick={() => {
                setSelectionMode(!selectionMode);
                if (selectionMode) setSelectedCabinets([]);
              }}
            >
              <Checkbox className="mr-2 h-4 w-4" checked={selectionMode} />
              Sélection
            </Button>
            {selectedCabinets.length > 0 && (
              <Button 
                onClick={() => setEmailDialogOpen(true)}
                className="bg-primary"
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer un email ({selectedCabinets.length})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCabinets.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground">Aucun cabinet trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  {selectionMode && (
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedCabinets.length === filteredCabinets.filter(c => c.email).length && filteredCabinets.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedCabinets(filteredCabinets.filter(c => c.email).map(c => ({
                              id: c.id,
                              email: c.email,
                              name: c.nom,
                              type: "cabinet"
                            })));
                          } else {
                            setSelectedCabinets([]);
                          }
                        }}
                      />
                    </TableHead>
                  )}
                  <TableHead className="w-[3%] text-center">N°</TableHead>
                  <TableHead className="w-[25%]">Cabinet</TableHead>
                  <TableHead className="w-[8%]">ID IGT</TableHead>
                  <TableHead className="w-[22%]">Secteur</TableHead>
                  <TableHead className="w-[10%]">Région</TableHead>
                  <TableHead className="w-[8%] text-center">Entrep.</TableHead>
                  <TableHead className="w-[10%]">Statut</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCabinets.map((cabinet, index) => (
                  <TableRow key={cabinet.id}>
                    {selectionMode && (
                      <TableCell>
                        <Checkbox 
                          checked={selectedCabinets.some(c => c.id === cabinet.id)}
                          disabled={!cabinet.email}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCabinets([...selectedCabinets, {
                                id: cabinet.id,
                                email: cabinet.email,
                                name: cabinet.nom,
                                type: "cabinet"
                              }]);
                            } else {
                              setSelectedCabinets(selectedCabinets.filter(c => c.id !== cabinet.id));
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell className="w-[3%] text-center font-medium text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell className="w-[25%]">
                      <div>
                        <p className="font-medium">{cabinet.nom}</p>
                        <p className="text-sm text-muted-foreground truncate">{cabinet.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="w-[8%]">
                      <span className="font-mono font-semibold text-primary">{cabinet.id_igt || "—"}</span>
                    </TableCell>
                    <TableCell className="w-[22%]">
                      <span className="text-sm">{cabinet.secteur_activite || "—"}</span>
                    </TableCell>
                    <TableCell className="w-[10%]">
                      <span className="text-sm font-medium">{cabinet.region || "—"}</span>
                    </TableCell>
                    <TableCell className="w-[8%] text-center">
                      <span className="font-semibold">{cabinet.entreprises_count}</span>
                    </TableCell>
                    <TableCell className="w-[10%]">{getStatusBadge(cabinet.status)}</TableCell>
                    <TableCell className="w-[10%] text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/cabinets/${cabinet.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(cabinet)}>
                              <FileText className="w-4 h-4 mr-2 text-slate-600" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => fetchCabinetDocuments(cabinet)}>
                              <FolderOpen className="w-4 h-4 mr-2 text-blue-600" />
                              Voir Documents
                            </DropdownMenuItem>
                            {cabinet.status === "pre_autorise" && (
                              <DropdownMenuItem 
                                onClick={() => openValidationDialog(cabinet)}
                                disabled={validating}
                                data-testid={`validate-cabinet-${cabinet.id}`}
                              >
                                <ShieldCheck className="w-4 h-4 mr-2 text-emerald-600" />
                                Valider et Habiliter
                              </DropdownMenuItem>
                            )}
                            {cabinet.status === "actif" && (
                              <>
                                <DropdownMenuItem onClick={() => downloadHabilitationPdf(cabinet)} data-testid={`download-habilitation-${cabinet.id}`}>
                                  <Award className="w-4 h-4 mr-2 text-emerald-600" />
                                  Télécharger Habilitation
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => resendHabilitationEmail(cabinet)} data-testid={`resend-habilitation-${cabinet.id}`}>
                                  <Send className="w-4 h-4 mr-2 text-blue-600" />
                                  Renvoyer email habilitation
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => resetCabinetPassword(cabinet)} data-testid={`reset-password-${cabinet.id}`}>
                                  <RefreshCw className="w-4 h-4 mr-2 text-orange-600" />
                                  Réinitialiser mot de passe
                                </DropdownMenuItem>
                              </>
                            )}
                            {cabinet.status !== "actif" && cabinet.status !== "pre_autorise" && (
                              <DropdownMenuItem onClick={() => handleStatusChange(cabinet.id, "actif")}>
                                <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                Réactiver
                              </DropdownMenuItem>
                            )}
                            {cabinet.status !== "suspendu" && cabinet.status !== "pre_autorise" && (
                              <DropdownMenuItem onClick={() => handleStatusChange(cabinet.id, "suspendu")}>
                                <Pause className="w-4 h-4 mr-2 text-amber-600" />
                                Suspendre
                              </DropdownMenuItem>
                            )}
                            {cabinet.status !== "revoque" && (
                              <DropdownMenuItem onClick={() => handleStatusChange(cabinet.id, "revoque")}>
                                <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                Révoquer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => !resetPasswordDialog.loading && setResetPasswordDialog({ open, cabinet: null, loading: false, result: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className={`w-5 h-5 ${resetPasswordDialog.loading ? 'animate-spin text-blue-600' : 'text-emerald-600'}`} />
              {resetPasswordDialog.loading ? "Réinitialisation en cours..." : "Mot de passe réinitialisé"}
            </DialogTitle>
            {resetPasswordDialog.cabinet && (
              <DialogDescription>
                {resetPasswordDialog.cabinet.nom} ({resetPasswordDialog.cabinet.id_igt})
              </DialogDescription>
            )}
          </DialogHeader>
          
          {resetPasswordDialog.loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-sm text-gray-500">Génération du nouveau mot de passe...</p>
            </div>
          ) : resetPasswordDialog.result && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-700 mb-3">
                  ✅ Le nouveau mot de passe a été généré et envoyé par email.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-white rounded p-2 border">
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-mono text-sm">{resetPasswordDialog.result.email}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(resetPasswordDialog.result.email, "Email")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between bg-white rounded p-2 border border-orange-200">
                    <div>
                      <p className="text-xs text-gray-500">Nouveau mot de passe</p>
                      <p className="font-mono text-sm font-bold text-orange-600">{resetPasswordDialog.result.new_password}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(resetPasswordDialog.result.new_password, "Mot de passe")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  ⚠️ <strong>Important :</strong> Le cabinet devra changer ce mot de passe lors de sa première connexion.
                </p>
              </div>
              
              <Button 
                className="w-full"
                variant="outline"
                onClick={() => {
                  const text = `Plateforme IGT Bilan Social\nEmail: ${resetPasswordDialog.result.email}\nMot de passe: ${resetPasswordDialog.result.new_password}`;
                  copyToClipboard(text, "Identifiants");
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier tous les identifiants
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setResetPasswordDialog({ open: false, cabinet: null, loading: false, result: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Habilitation Email Dialog */}
      <Dialog open={resendHabilitationDialog.open} onOpenChange={(open) => !resendHabilitationDialog.loading && setResendHabilitationDialog({ open, cabinet: null, loading: false, result: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className={`w-5 h-5 ${resendHabilitationDialog.loading ? 'animate-spin text-blue-600' : 'text-emerald-600'}`} />
              {resendHabilitationDialog.loading ? "Envoi en cours..." : "Email d'habilitation envoyé"}
            </DialogTitle>
            {resendHabilitationDialog.cabinet && (
              <DialogDescription>
                {resendHabilitationDialog.cabinet.nom} ({resendHabilitationDialog.cabinet.id_igt})
              </DialogDescription>
            )}
          </DialogHeader>
          
          {resendHabilitationDialog.loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-sm text-gray-500">Envoi de l'email d'habilitation complet...</p>
            </div>
          ) : resendHabilitationDialog.result && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-700 mb-3">
                  ✅ L'email d'habilitation complet a été renvoyé avec succès, incluant :
                </p>
                <ul className="text-sm text-emerald-600 list-disc list-inside space-y-1 mb-3">
                  <li>La notification officielle d'habilitation</li>
                  <li>Les identifiants de connexion</li>
                  <li>Le PDF de la décision d'habilitation</li>
                  <li>Le PDF des identifiants</li>
                </ul>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-white rounded p-2 border">
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-mono text-sm">{resendHabilitationDialog.result.cabinet_email}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(resendHabilitationDialog.result.cabinet_email, "Email")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {resendHabilitationDialog.result.new_password && (
                    <div className="flex items-center justify-between bg-white rounded p-2 border border-orange-200">
                      <div>
                        <p className="text-xs text-gray-500">Nouveau mot de passe</p>
                        <p className="font-mono text-sm font-bold text-orange-600">{resendHabilitationDialog.result.new_password}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(resendHabilitationDialog.result.new_password, "Mot de passe")}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between bg-white rounded p-2 border">
                    <div>
                      <p className="text-xs text-gray-500">ID IGT</p>
                      <p className="font-mono text-sm">{resendHabilitationDialog.result.id_igt}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(resendHabilitationDialog.result.id_igt, "ID IGT")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  ⚠️ <strong>Important :</strong> Le cabinet devra changer ce mot de passe lors de sa première connexion.
                </p>
              </div>
              
              {resendHabilitationDialog.result.new_password && (
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => {
                    const text = `Plateforme IGT Bilan Social\nEmail: ${resendHabilitationDialog.result.cabinet_email}\nMot de passe: ${resendHabilitationDialog.result.new_password}\nID IGT: ${resendHabilitationDialog.result.id_igt}`;
                    copyToClipboard(text, "Identifiants");
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier tous les identifiants
                </Button>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setResendHabilitationDialog({ open: false, cabinet: null, loading: false, result: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <SendEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        recipients={selectedCabinets}
        recipientType="cabinet"
        allowGroupSelection={true}
        title="Envoyer un email aux cabinets"
      />

      {/* Documents Dialog */}
      <Dialog open={documentsDialog.open} onOpenChange={(open) => setDocumentsDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              Documents du Cabinet
            </DialogTitle>
            <DialogDescription>
              {documentsDialog.cabinet?.nom} - {documentsDialog.cabinet?.id_igt}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {documentsDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : documentsDialog.documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucun document n'a été téléchargé pour ce cabinet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documentsDialog.documents.map((doc, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className={`h-8 w-8 ${doc.exists ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div>
                        <div className="font-medium">{doc.type}</div>
                        <div className="text-sm text-muted-foreground">{doc.name}</div>
                        {doc.uploaded_at && (
                          <div className="text-xs text-muted-foreground">
                            Uploadé le {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.exists ? (
                        <>
                          {doc.name.toLowerCase().endsWith('.pdf') && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => previewDocument(doc.download_url, doc.name)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Aperçu
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => downloadDocument(doc.download_url, doc.name)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Télécharger
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-red-500 px-2 py-1 bg-red-50 rounded">
                          Fichier non trouvé
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentsDialog(prev => ({ ...prev, open: false }))}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <PDFPreview
        url={pdfPreview.url}
        filename={pdfPreview.filename}
        open={pdfPreview.open}
        onClose={closePdfPreview}
      />

      {/* Credentials Dialog - Affichage des identifiants après validation */}
      <Dialog open={credentialsDialog.open} onOpenChange={(open) => setCredentialsDialog({ open, credentials: open ? credentialsDialog.credentials : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="h-6 w-6" />
              Cabinet Validé avec Succès !
            </DialogTitle>
            <DialogDescription>
              Voici les identifiants de connexion du cabinet. Transmettez-les au responsable.
            </DialogDescription>
          </DialogHeader>
          
          {credentialsDialog.credentials && (
            <div className="space-y-4">
              {/* Info Cabinet */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <h4 className="font-semibold text-emerald-800 mb-2">{credentialsDialog.credentials.cabinet_nom}</h4>
                <p className="text-sm text-emerald-700">N° Habilitation: <span className="font-mono font-bold">{credentialsDialog.credentials.id_igt}</span></p>
                <p className="text-sm text-emerald-700">Responsable: {credentialsDialog.credentials.responsable}</p>
              </div>

              {/* Identifiants */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Plateforme</p>
                    <p className="font-mono text-sm">{credentialsDialog.credentials.platform_url}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(credentialsDialog.credentials.platform_url, "URL")}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <p className="text-xs text-blue-600">Identifiant (Email)</p>
                    <p className="font-mono text-sm font-bold text-blue-800">{credentialsDialog.credentials.email}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(credentialsDialog.credentials.email, "Email")}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div>
                    <p className="text-xs text-red-600">Mot de passe initial</p>
                    <p className="font-mono text-lg font-bold text-red-700">{credentialsDialog.credentials.password}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(credentialsDialog.credentials.password, "Mot de passe")}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Avertissement */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important :</strong> Le cabinet devra changer son mot de passe lors de la première connexion.
                </p>
              </div>

              {/* Bouton copier tout */}
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  const text = `IDENTIFIANTS DE CONNEXION - IGT Bilan Social\n\nCabinet: ${credentialsDialog.credentials.cabinet_nom}\nN° Habilitation: ${credentialsDialog.credentials.id_igt}\n\nPlateforme: ${credentialsDialog.credentials.platform_url}\nIdentifiant: ${credentialsDialog.credentials.email}\nMot de passe: ${credentialsDialog.credentials.password}\n\n⚠️ Changez votre mot de passe lors de la première connexion.`;
                  copyToClipboard(text, "Tous les identifiants");
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copier tous les identifiants
              </Button>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => credentialsDialog.credentials && downloadCredentialsPdf(credentialsDialog.credentials.cabinet_id)}
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger PDF
            </Button>
            <Button onClick={() => setCredentialsDialog({ open: false, credentials: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de validation avec observations */}
      <Dialog open={validationDialog.open} onOpenChange={(open) => {
        if (!open) {
          setValidationDialog({ open: false, cabinet: null });
          setValidationObservations("");
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="w-5 h-5" />
              Valider et Habiliter le Cabinet
            </DialogTitle>
            <DialogDescription>
              {validationDialog.cabinet && (
                <>Validation du cabinet <strong>{validationDialog.cabinet.nom}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {validationDialog.cabinet && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm"><strong>Cabinet :</strong> {validationDialog.cabinet.nom}</p>
                <p className="text-sm"><strong>RCCM :</strong> {validationDialog.cabinet.rccm}</p>
                <p className="text-sm"><strong>Email :</strong> {validationDialog.cabinet.email}</p>
                <p className="text-sm"><strong>Responsable :</strong> {validationDialog.cabinet.responsable_prenom} {validationDialog.cabinet.responsable_nom}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="observations" className="text-sm font-medium">
                Observations (optionnel)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Ces observations seront incluses dans l'email d'habilitation mais pas dans le PDF officiel.
              </p>
              <textarea
                id="observations"
                className="w-full min-h-[120px] p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Ajoutez vos observations ou instructions particulières pour ce cabinet..."
                value={validationObservations}
                onChange={(e) => setValidationObservations(e.target.value)}
              />
            </div>
            
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>Actions effectuées lors de la validation :</strong>
              </p>
              <ul className="text-sm text-emerald-700 list-disc pl-5 mt-2 space-y-1">
                <li>Création du compte utilisateur</li>
                <li>Envoi de l'email d'habilitation avec les identifiants</li>
                <li>Envoi du PDF de la décision d'habilitation</li>
                <li>Envoi du PDF des identifiants de connexion</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setValidationDialog({ open: false, cabinet: null })}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleValidateCabinet}
              disabled={validating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {validating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validation en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Valider et Habiliter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de modification du cabinet */}
      <Dialog open={editDialog.open} onOpenChange={(open) => {
        if (!open) {
          setEditDialog({ open: false, cabinet: null });
        }
      }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Modifier le Cabinet
            </DialogTitle>
            <DialogDescription>
              {editDialog.cabinet && (
                <>Modification du cabinet <strong>{editDialog.cabinet.nom}</strong> (N° {editDialog.cabinet.id_igt})</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Informations générales */}
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-3">Informations générales</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-nom">Nom du cabinet</Label>
                  <Input
                    id="edit-nom"
                    value={editFormData.nom}
                    onChange={(e) => setEditFormData({...editFormData, nom: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-secteur">Secteur d'activité</Label>
                  <Input
                    id="edit-secteur"
                    value={editFormData.secteur_activite}
                    onChange={(e) => setEditFormData({...editFormData, secteur_activite: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-region">Région</Label>
                  <Select 
                    value={editFormData.region} 
                    onValueChange={(value) => setEditFormData({...editFormData, region: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une région" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIONS.map((region) => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-adresse">Adresse</Label>
                  <Input
                    id="edit-adresse"
                    value={editFormData.adresse}
                    onChange={(e) => setEditFormData({...editFormData, adresse: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-3">Contact</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-telephone">Téléphone</Label>
                  <Input
                    id="edit-telephone"
                    value={editFormData.telephone}
                    onChange={(e) => setEditFormData({...editFormData, telephone: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Responsable */}
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-3">Responsable</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-resp-prenom">Prénom</Label>
                  <Input
                    id="edit-resp-prenom"
                    value={editFormData.responsable_prenom}
                    onChange={(e) => setEditFormData({...editFormData, responsable_prenom: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-resp-nom">Nom</Label>
                  <Input
                    id="edit-resp-nom"
                    value={editFormData.responsable_nom}
                    onChange={(e) => setEditFormData({...editFormData, responsable_nom: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-3">Documents ({editDocuments.length})</h4>
              <p className="text-xs text-muted-foreground mb-3">
                Vous pouvez ajouter de nouveaux documents ou supprimer des documents existants.
              </p>
              
              <div className="space-y-3">
                {DOCUMENT_TYPES.map((docType) => {
                  const docsOfType = editDocuments.filter(d => d.type === docType.value);
                  const docCount = docsOfType.length;
                  
                  return (
                    <div key={docType.value} className="border rounded-lg p-3 bg-slate-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-sm">{docType.label}</span>
                          {docCount > 0 && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                              {docCount}
                            </span>
                          )}
                        </div>
                        <label className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded-md cursor-pointer hover:bg-primary/90">
                          <Plus className="w-3 h-3" />
                          Ajouter
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            multiple
                            onChange={(e) => handleEditDocumentUpload(e, docType.value)}
                            disabled={uploadingFile}
                          />
                        </label>
                      </div>
                      
                      {docCount > 0 && (
                        <div className="space-y-1 mt-2">
                          {docsOfType.map((doc, idx) => {
                            const globalIndex = editDocuments.findIndex(d => d === doc);
                            return (
                              <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                  <span className="text-xs text-slate-600 truncate">{doc.original_name}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeEditDocument(globalIndex)}
                                  className="p-1 hover:bg-red-50 rounded text-red-500 flex-shrink-0"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {uploadingFile && (
                <div className="flex items-center gap-2 text-sm text-primary mt-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Téléchargement en cours...
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEditDialog({ open: false, cabinet: null })}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
